/**
 * 多通貨ウォレットの見た目(docs/10_paltown_platform.md §4.3)。
 *
 * 残高そのものはサーバーが権威(CLAUDE.md ルール2)。ここが持つのは
 * **通貨キー → 画面に出す名前とアイコン**の対応だけで、値は1つも持たない。
 *
 * `@paltyping/quiz` は**型としてすら import しない**。値を import すると
 * まなびの遅延読込チャンクが初期バンドルに載ってしまうため(docs/10 §2)。
 * 教科名は素の文字列で扱う(`services/quizRewards.ts` と同じ割り切り)。
 */

/** タイピングのコイン(旧・単一 coins)。 */
export const TYPING_CURRENCY = 'typing';

/**
 * チャレンジのトロフィーコイン(docs/12 §3)。
 *
 * 増えるのは**チャレンジの完走・順位報酬とリーグの精算だけ**。
 * まなびのコインと案内の文言が違うので、画面はこのキーで出し分ける。
 */
export const TROPHY_CURRENCY = 'trophy';

/**
 * 思考力パズルのコイン(docs/11 §3)。
 *
 * まなびの「ちえ」もここに入る(docs/13 §9.1 で ちえ = 思考力領域として統合)。
 * 「パズルのもり」でも「ちえ」の単元でも貯まるので、案内の文言だけ他と違う。
 */
export const PUZZLE_CURRENCY = 'puzzle';

export interface CurrencyLabel {
  /** コインの呼び名(ひらがな。CLAUDE.md ルール5)。 */
  readonly name: string;
  /** 残高の前に出すアイコン(絵文字 or 記号)。 */
  readonly icon: string;
}

/**
 * 通貨キー → 表示(docs/13 §9.1)。
 *
 * **キーは普遍領域**(math/language/…)で、日本の教科名ではない。
 * 表示名はここで日本のパックに合わせてローカライズする:
 * 帯の数のうえで多数を占める小学生の教科名を採り、`math` は「さんすうコイン」、
 * `language` は「こくごコイン」と呼ぶ。幼児帯の「かず」「もじ」の単元でも
 * 同じコインなので、ガチャ画面ではこの名前で出る(docs/13 §9.1 が許す
 * 「文脈でのローカライズ」の逆で、**教科の文脈が無い画面での既定名**)。
 * まなびの画面は教科名そのものを使う(`ui/learn/labels.ts` の `learnCoinName`)。
 *
 * 絵文字は `ui/learn/labels.ts` の `LEARN_SUBJECT_EMOJI` から、その領域の
 * 代表教科のものを使う(同じ絵が同じコインを指すようにするため)。
 */
export const CURRENCY_LABELS: Readonly<Record<string, CurrencyLabel>> = {
  typing: { name: 'コイン', icon: '◎' },
  puzzle: { name: 'パズルコイン', icon: '🧩' },
  trophy: { name: 'トロフィーコイン', icon: '🏆' },
  math: { name: 'さんすうコイン', icon: '➕' },
  language: { name: 'こくごコイン', icon: '📖' },
  science: { name: 'りかコイン', icon: '🔬' },
  social: { name: 'しゃかいコイン', icon: '🗺️' },
  life: { name: 'せいかつコイン', icon: '🌳' },
};

/**
 * まなびの教科名 → 通貨キー(docs/13 §9.1 の確定表)。
 *
 * **サーバー側の正は `functions/src/config.ts` の `QUIZ_CURRENCY_BY_SUBJECT`**。
 * ここはリザルトから「その教科のガチャ」へ案内し、残高キャッシュを
 * 通貨ごとに持つための写しなので、知らない教科は `undefined`
 * (案内を出さない・キャッシュしない = 画面は壊れない)。
 */
export const CURRENCY_BY_SUBJECT: Readonly<Record<string, string>> = {
  かず: 'math',
  さんすう: 'math',
  もじ: 'language',
  こくご: 'language',
  ちえ: PUZZLE_CURRENCY,
  せいかつ: 'life',
  りか: 'science',
  しゃかい: 'social',
};

/**
 * まなびの教科名 → その教科のガチャID。
 *
 * 通貨が統合された(かず・さんすう → `math`)ので、**通貨だけでは
 * どのガチャを開けばいいか決まらない**。リザルトからは「いま遊んだ教科の
 * ガチャ」を開きたいので、ここで教科ごとの入口を持つ。
 * カタログに無いIDのときは通貨で解決される(`GachaScreen`)ので、
 * ガチャが消えても画面は壊れない。
 */
export const GACHA_ID_BY_SUBJECT: Readonly<Record<string, string>> = {
  かず: 'kazu-01',
  さんすう: 'sansu-01',
  もじ: 'moji-01',
  こくご: 'kokugo-01',
  ちえ: 'chie-01',
  せいかつ: 'seikatsu-01',
  りか: 'rika-01',
  しゃかい: 'shakai-01',
};

/**
 * そのコインが**どこで貯まるか**の案内(ひらがな。CLAUDE.md ルール5)。
 *
 * ガチャ画面で残高が足りないときに出す。トロフィーコインはまなびでは
 * 1枚も貯まらない(チャレンジ・リーグの報酬だけ。docs/12 §3)ので、
 * 教科コインと同じ文言にしてはいけない。パズルコインは**両方**で貯まる
 * (パズルのもり + まなびの「ちえ」。docs/13 §9.1)。
 */
export function coinSourceHint(currency: string): string {
  if (currency === TYPING_CURRENCY) return 'コインは ステージを クリアすると もらえるよ';
  const name = currencyLabel(currency).name;
  if (currency === TROPHY_CURRENCY) return `${name}は チャレンジで もらえるよ`;
  if (currency === PUZZLE_CURRENCY) return `${name}は パズルのもりと ちえの もんだいで たまるよ`;
  return `${name}は まなびモードで もんだいに こたえると たまるよ`;
}

const FALLBACK: CurrencyLabel = { name: 'コイン', icon: '◎' };

/** 知らない通貨でも画面を壊さない(コンテンツのほうが新しいときに起きる)。 */
export function currencyLabel(currency: string): CurrencyLabel {
  return CURRENCY_LABELS[currency] ?? FALLBACK;
}
